import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { api, mediaUrl } from '../../services/api';

const LABELS = {
  leader: 'Fotos do líder/co-líder',
  host: 'Fotos do anfitrião/casa',
  meeting: 'Fotos das reuniões',
};

// Upload de imagens por categoria (líder: 2, anfitrião: 2, reunião: 5) com
// drag & drop, contador de limite e remoção individual.
export default function CellMediaUploader({ cellId, category, photos = [], limit, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const remaining = limit - photos.length;

  async function upload(fileList) {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    if (files.length > remaining) {
      toast.error(`Só é possível enviar mais ${remaining} foto(s) nesta categoria (limite de ${limit}).`);
      return;
    }
    setUploading(true);
    try {
      const updated = await api.uploadCellMedia(cellId, category, files);
      onChange(updated);
      toast.success('Fotos enviadas!');
    } catch (err) {
      toast.error(err.message || 'Falha ao enviar fotos.');
    } finally {
      setUploading(false);
    }
  }

  async function removePhoto(url) {
    try {
      const updated = await api.deleteCellMedia(cellId, category, url);
      onChange(updated);
    } catch (err) {
      toast.error(err.message || 'Falha ao remover foto.');
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer.files);
  }

  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">
          {LABELS[category]}
        </span>
        <span className={`text-xs font-bold ${remaining <= 0 ? 'text-rose-500' : 'text-ink-soft'}`}>
          {photos.length}/{limit} fotos
        </span>
      </div>

      {photos.length > 0 && (
        <div className="mb-2 grid grid-cols-3 gap-2">
          {photos.map((url) => (
            <div key={url} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200">
              <img src={mediaUrl(url)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(url)}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-ink/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                aria-label="Remover foto"
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {remaining > 0 ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`flex h-24 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-center transition-colors ${
            dragOver ? 'border-primary bg-primary-light' : 'border-slate-200 bg-slate-50 hover:border-primary/60'
          }`}
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin text-primary" />
          ) : (
            <>
              <Upload size={18} className="text-ink-faint" />
              <span className="mt-1 text-xs text-ink-soft">Arraste ou clique para enviar</span>
            </>
          )}
        </div>
      ) : (
        <div className="flex h-16 items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs text-ink-faint">
          <ImageOff size={15} /> Limite de fotos atingido
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ''; }}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
